import React, { useState } from 'react';
import { Phone, Mail, MapPin, Loader2, ExternalLink, FileText, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { contactInfo } from '../mockData';
import { toast } from 'sonner';
import axios from 'axios';

const iconMap = {
  Phone: Phone,
  Mail: Mail,
  MapPin: MapPin,
  FileText: FileText,
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Poruka poslana! Javit ćemo vam se uskoro.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Greška pri slanju poruke. Pokušajte ponovno.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-12 md:py-16 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-12">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-0.5 bg-[#cab28e] mr-4" />
            <span className="text-[#cab28e] text-sm tracking-widest font-semibold">
              {contactInfo.subtitle}
            </span>
            <div className="w-16 h-0.5 bg-[#cab28e] ml-4" />
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6">
            STUPITE U <span className="text-[#cab28e]">KONTAKT</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {contactInfo.description}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-4">
            {/* Contact Cards */}
            {[contactInfo.phone, contactInfo.email, contactInfo.oib, contactInfo.address].map((item, index) => {
              const IconComponent = iconMap[item.icon];
              return (
                <div
                  key={index}
                  className="flex items-start space-x-4 p-5 bg-gray-900/50 border border-gray-800 rounded-lg md:rounded-xl hover:border-[#cab28e]/50 transition-colors duration-300"
                >
                  <div className="w-12 h-12 bg-[#cab28e]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-6 h-6 text-[#cab28e]" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1 tracking-wide">
                      {item.label}
                    </h3>
                    {item.icon === 'Phone' ? (
                      <a
                        href={`tel:${item.value.replace(/\s/g, '')}`}
                        data-testid="contact-phone-link"
                        className="text-gray-400 hover:text-[#cab28e] transition-colors duration-200"
                      >
                        {item.value}
                      </a>
                    ) : item.icon === 'Mail' ? (
                      <a
                        href={`mailto:${item.value}`}
                        className="text-gray-400 hover:text-[#cab28e] transition-colors duration-200"
                      >
                        {item.value}
                      </a>
                    ) : item.icon === 'MapPin' ? (
                      <a
                        href="https://maps.app.goo.gl/V1crftxr3vzPYsmm8"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-address-link"
                        className="text-gray-400 hover:text-[#cab28e] transition-colors duration-200"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-gray-400" data-testid="contact-oib-value">{item.value}</p>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Radno vrijeme */}
            <div className="flex items-start space-x-4 p-5 bg-gray-900/50 border border-gray-800 rounded-lg md:rounded-xl hover:border-[#cab28e]/50 transition-colors duration-300">
              <div className="w-12 h-12 bg-[#cab28e]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-[#cab28e]" />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1 tracking-wide">Radno vrijeme</h3>
                <div className="space-y-1 text-gray-400">
                  <p>Ponedjeljak - Petak: 10:00 - 18:30</p>
                  <p>Subota - Nedjelja: Zatvoreno</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <form id="contact-form" onSubmit={handleSubmit} className="space-y-6 scroll-mt-36 md:scroll-mt-40" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-300 mb-2 font-medium md:text-sm md:tracking-wide">
                    Ime i prezime
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="contact-name-input"
                    className="bg-gray-900 border-gray-800 text-white focus:border-[#cab28e] focus-visible:ring-[#cab28e]/40 transition-colors duration-200"
                    placeholder="Vaše ime"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-2 font-medium md:text-sm md:tracking-wide">
                    Email
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="contact-email-input"
                    className="bg-gray-900 border-gray-800 text-white focus:border-[#cab28e] focus-visible:ring-[#cab28e]/40 transition-colors duration-200"
                    placeholder="vas.email@primjer.hr"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-300 mb-2 font-medium md:text-sm md:tracking-wide">
                  Telefon
                </label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  data-testid="contact-phone-input"
                  className="bg-gray-900 border-gray-800 text-white focus:border-[#cab28e] focus-visible:ring-[#cab28e]/40 transition-colors duration-200"
                  placeholder="+385 xx xxx xxxx"
                />
              </div>

              <div>
                <label className="block text-gray-300 mb-2 font-medium md:text-sm md:tracking-wide">
                  Poruka
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  data-testid="contact-message-input"
                  className="bg-gray-900 border-gray-800 text-white focus:border-[#cab28e] focus-visible:ring-[#cab28e]/40 transition-colors duration-200 resize-none"
                  placeholder="Opišite vaš projekt ili upit..."
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                data-testid="contact-submit-button"
                className="w-full bg-[#cab28e] hover:bg-[#dfc8a2] text-black py-6 text-lg font-semibold md:font-bold md:uppercase md:tracking-[0.08em] rounded-md md:rounded-lg transition-all duration-300 md:hover:scale-[1.01] md:hover:shadow-[0_8px_28px_rgba(202,178,142,0.3)] disabled:opacity-70 disabled:hover:scale-100"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    SLANJE...
                  </span>
                ) : (
                  'POŠALJITE PORUKU'
                )}
              </Button>
            </form>
          </div>
        </div>

        {/* Map (left) + Office photo (right) — two equal columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mt-10 md:mt-14">
          <div className="order-2 md:order-1 rounded-lg md:rounded-xl overflow-hidden border border-gray-800">
            <iframe
              title="Nova-Stron lokacija"
              data-testid="contact-map-iframe"
              src="https://www.google.com/maps?q=Nova-Stron%20d.o.o.%2C%20Ul.%20Ante%20Star%C4%8Devi%C4%87a%2032%2C%2021300%20Makarska&output=embed"
              width="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="block w-full h-56 md:h-64 grayscale-[30%]"
            />
          </div>
          <div className="order-1 md:order-2 relative rounded-lg md:rounded-xl overflow-hidden border border-gray-800">
            <img
              src="/projects/office.webp"
              alt="Ured Nova-Stron"
              loading="lazy"
              data-testid="contact-office-photo"
              className="block w-full h-56 md:h-64 object-cover"
            />
            <a
              href="https://maps.app.goo.gl/V1crftxr3vzPYsmm8"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-map-link"
              className="absolute bottom-3 left-3 flex items-center gap-2 px-4 py-2 rounded-md bg-black/70 hover:bg-black/90 backdrop-blur-sm transition-colors duration-300"
            >
              <MapPin className="w-4 h-4 text-[#cab28e]" />
              <span className="text-white text-sm font-medium">Prikaži na karti</span>
              <ExternalLink className="w-3.5 h-3.5 text-[#cab28e]" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
